import { QueryObserverResult, useQueries } from '@tanstack/react-query';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT } from 'components/pages/ProfitPoolOverview/apiUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { useContext, useEffect, useState } from 'react';
import { useProfitPoolEvolutionContext } from '../context/ProfitPoolEvolutionContext';
import { isEmpty, sortBy } from 'lodash';
import dayjs from 'dayjs';
import { getPeriodLabel } from '../utils/ppevolutionUtils';

const useKPIEvolutionData = (selectedKPI: { key: string; label: string }) => {
    const [offset, setOffset] = useState(0);
    const {
        multiSelectedTiers,
        idForRestOfComponent,
        timePeriodSelection,
        allDimAndAttributeTypeList,
        timePeriodLoading,
    } = useContext(FilterContextData);
    const { evolutionFrequency } = useProfitPoolEvolutionContext();
    const [allData, setAllData] = useState<any[]>([]);
    const [min, setMin] = useState<number>(0);
    const [max, setMax] = useState<number>(0);

    const fetchNextPage = () => {
        setOffset((prevOffset) => prevOffset + PROFIT_POOL_API_LIMIT);
    };

    const { data, isPending, isError, isSuccess } = useQueries({
        queries: [true, false].map((_yearAgo: boolean) => {
            return {
                queryKey: [
                    'kpi-evolution-data',
                    evolutionFrequency,
                    idForRestOfComponent,
                    offset,
                    _yearAgo,
                    timePeriodSelection,
                    selectedKPI?.key,
                ],
                queryFn: ({ signal }) =>
                    fetchProfitPoolData({
                        selectedSubFilter: { dataKey: '', dimension: '', isAttribute: false },
                        kpi: selectedKPI?.key,
                        tierList: multiSelectedTiers,
                        yearAgo: _yearAgo,
                        groupedByList: ['period'],
                        dimIdsFilter: idForRestOfComponent,
                        frequency: evolutionFrequency,
                        timePeriod: JSON.parse(timePeriodSelection),
                        signal,
                    }),
                enabled: Boolean(
                    allDimAndAttributeTypeList &&
                        'loading' in allDimAndAttributeTypeList &&
                        !timePeriodLoading &&
                        timePeriodSelection,
                ),
            };
        }),
        combine: (results: QueryObserverResult<any, Error>[]) => {
            return {
                data: results.map((result) => result.data?.data),
                isPending: results.some((result) => result.isPending),
                isError: results.some((result) => result.isError),
                isSuccess: results.every((result) => result.isSuccess),
            };
        },
    });

    const getKPIEvolutionData = (currentYearResult: any, yearAgoResult: any) => {
        const finalData = [];
        let minProfit = Number.POSITIVE_INFINITY;
        let maxProfit = Number.NEGATIVE_INFINITY;

        const yearAgoMap = new Map();
        yearAgoResult?.forEach((item: any) => {
            yearAgoMap.set(`${item.period}-${item.tier}`, item);
        });

        for (let i = 0; i < currentYearResult?.length; i++) {
            const currentYearItem = currentYearResult[i];
            const previousYearPeriod = dayjs(currentYearItem?.period, 'YYYY[M]MM')
                .subtract(1, 'year')
                .format('YYYY[M]MM');
            const previousYearItem = yearAgoMap.get(`${previousYearPeriod}-${currentYearItem?.tier}`);
            const currentYearKPIValue = currentYearItem?.kpi_values?.[selectedKPI?.key];
            const previousYearKPIValue = previousYearItem?.kpi_values?.[selectedKPI?.key] || 0;

            const periodLabel = getPeriodLabel(currentYearItem?.period);

            if (currentYearKPIValue < minProfit) minProfit = currentYearKPIValue;
            if (currentYearKPIValue > maxProfit) maxProfit = currentYearKPIValue;
            if (previousYearKPIValue < minProfit) minProfit = previousYearKPIValue;
            if (previousYearKPIValue > maxProfit) maxProfit = previousYearKPIValue;

            finalData.push({
                period: periodLabel,
                kpiName: `${selectedKPI?.label} (YA)`,
                type: `${selectedKPI?.label} (YA)`,
                kpiValue: previousYearKPIValue,
            });
            finalData.push({
                period: periodLabel,
                kpiName: selectedKPI?.label,
                type: selectedKPI?.label,
                kpiValue: currentYearKPIValue,
            });
        }

        return { finalData, minProfit, maxProfit };
    };

    useEffect(() => {
        if (isSuccess && !isEmpty(data)) {
            const [yearAgoResult, currentYearResult] = data.map((_data) => {
                return sortBy(_data, 'period');
            });

            if (offset === 0) {
                const { finalData, minProfit, maxProfit } = getKPIEvolutionData(currentYearResult, yearAgoResult);
                setAllData(finalData);
                setMin(minProfit === Number.POSITIVE_INFINITY ? 0 : minProfit);
                setMax(maxProfit === Number.NEGATIVE_INFINITY ? 0 : maxProfit);
            } else {
                const { finalData, minProfit, maxProfit } = getKPIEvolutionData(currentYearResult, yearAgoResult);
                const mergedData = [...allData, ...finalData];
                setAllData(mergedData);
                setMin(minProfit === Number.POSITIVE_INFINITY ? 0 : minProfit);
                setMax(maxProfit === Number.NEGATIVE_INFINITY ? 0 : maxProfit);
            }
        } else if (isEmpty(data) && isEmpty(allData)) {
            setAllData([]);
            setMin(0);
            setMax(0);
        }
    }, [isSuccess, data, offset]);

    return {
        data: allData,
        isPending,
        isError,
        isSuccess,
        fetchNextPage,
        min,
        max,
    };
};

export default useKPIEvolutionData;
